import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/services/firebase';
import {
  parseInviteLookupPublicProfile,
  type InviteLookupPublicProfile,
} from '@/services/chatCryptoFirestore';

/** Подписка на inviteLookup по токенам — имя и аватар; новые токены подписываются без сноса уже существующих слушателей. */
export function usePeerInviteProfiles(inviteTokenIds: readonly string[]): {
  profiles: Record<string, InviteLookupPublicProfile>;
  loadedByToken: Record<string, boolean>;
} {
  const [profiles, setProfiles] = useState<Record<string, InviteLookupPublicProfile>>({});
  const [loadedByToken, setLoadedByToken] = useState<Record<string, boolean>>({});
  const unsubsRef = useRef(new Map<string, () => void>());

  const idsKey = useMemo(() => {
    const sorted = Array.from(new Set(inviteTokenIds.filter(Boolean))).sort();
    try {
      return JSON.stringify(sorted);
    } catch {
      return '[]';
    }
  }, [inviteTokenIds]);

  useEffect(() => {
    let ids: Set<string>;
    try {
      const parsed = JSON.parse(idsKey) as unknown;
      const arr = Array.isArray(parsed) ? parsed : [];
      ids = new Set(arr.filter((t): t is string => typeof t === 'string' && t.length > 0));
    } catch {
      ids = new Set();
    }

    const map = unsubsRef.current;

    if (ids.size === 0) {
      map.forEach((u) => u());
      map.clear();
      setProfiles({});
      setLoadedByToken({});
      return;
    }

    for (const [token, unsub] of [...map.entries()]) {
      if (!ids.has(token)) {
        unsub();
        map.delete(token);
        setProfiles((p) => {
          if (!(token in p)) return p;
          const n = { ...p };
          delete n[token];
          return n;
        });
        setLoadedByToken((p) => {
          if (!(token in p)) return p;
          const n = { ...p };
          delete n[token];
          return n;
        });
      }
    }

    for (const token of ids) {
      if (map.has(token)) continue;
      const unsub = onSnapshot(
        doc(db, 'inviteLookup', token),
        (snap) => {
          const parsed = parseInviteLookupPublicProfile(
            snap.exists() ? (snap.data() as Record<string, unknown>) : undefined
          );
          setProfiles((prev) => ({ ...prev, [token]: parsed }));
          setLoadedByToken((prev) => ({ ...prev, [token]: true }));
        },
        () => {
          setProfiles((prev) => ({ ...prev, [token]: { displayName: null, photoURL: null } }));
          setLoadedByToken((prev) => ({ ...prev, [token]: true }));
        }
      );
      map.set(token, unsub);
    }
  }, [idsKey]);

  useEffect(() => {
    return () => {
      unsubsRef.current.forEach((u) => u());
      unsubsRef.current.clear();
    };
  }, []);

  return { profiles, loadedByToken };
}
